import classNames from 'classnames';
import { FC } from 'react';

import { useTranslation } from 'utils/i18n';
import { sortFields } from '../../constants/sortFields';
import Switch from '../switch';

export type SortOption = keyof typeof sortFields;
type ViewType = 'list' | 'map';

interface Props {
  propertiesTotal: number;
  roomsTotal: number;
  // The search state's default sort is 'Name', which is not a SortOption, so
  // this is a plain string; the change handler narrows on the way back out.
  currentSortBy?: string;
  currentViewType?: ViewType;
  changeSortBy: (direction: SortOption) => void;
  changeViewType: () => void;
  location?: string;
  sortOptions: SortOption[];
  loading: boolean;
}

const SearchResultFilters: FC<Props> = ({
  propertiesTotal,
  roomsTotal,
  currentSortBy = 'Distance',
  currentViewType = 'list',
  changeSortBy,
  changeViewType,
  location,
  sortOptions,
  loading = true,
}) => {
  const { t } = useTranslation('common');
  const selectClasses = classNames({
    'select-list': true,
    'select-list--disabled': currentViewType === 'map',
  });

  return (
    <div className="search-results">
      <h2 className="search-results__heading">{t('search_results.title')}</h2>

      <div className="search-results__inner">
        <h3 className="search-results__info">
          {!loading && (
            <>
              {location && <span>{location}</span>}
              <span>
                {t('search_results.total_rooms', {
                  count: propertiesTotal > 0 ? roomsTotal : 0,
                })}
              </span>
              {propertiesTotal > 1 && (
                <span>
                  {t('search_results.total_properties', {
                    count: propertiesTotal,
                  })}
                </span>
              )}
            </>
          )}
        </h3>

        <div className="search-results__filters">
          <div className="search-results__filter-item search-results__filter-item--map-view">
            <Switch
              label={t('search_results.view_type.label')}
              toggled={currentViewType === 'map'}
              onSwitchChange={changeViewType}
            />
          </div>
          <div className="search-results__filter-item search-results__filter-item--sortby">
            <div className={selectClasses}>
              <select
                data-testid="sortField"
                value={currentSortBy || ''}
                onChange={e =>
                  changeSortBy(e.currentTarget.value as SortOption)
                }
                disabled={currentViewType === 'map'}
              >
                {!!sortOptions.length &&
                  sortOptions.map(field => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultFilters;
